import { useEffect, useMemo, useState, useRef } from "react";
import { api, TYPE_LABELS, money } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Calculator, Save, RefreshCw, TrendingUp, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";

const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const marginTone = (m) => (m >= 40 ? "text-emerald-600" : m >= 20 ? "text-amber-600" : "text-rose-600");
// Where a price sits vs a [low, high] market band.
const band = (v, rng) => (!rng || !(v > 0) ? null : v < rng[0] ? "below" : v > rng[1] ? "above" : "within");
const BAND_STYLE = {
  below: { cls: "bg-amber-50 text-amber-700", label: "Below" },
  within: { cls: "bg-emerald-50 text-emerald-700", label: "In range" },
  above: { cls: "bg-blue-50 text-blue-700", label: "Above" },
};

// Profit for a full order of `units` at wholesale, after Faire commission + processing,
// paying Printify product cost per unit and shipping (first item + additional items).
function orderEconomics(row, units, commission, processing, absorbShipping) {
  const revenue = row.wholesale * units;
  const commissionAmt = revenue * (commission / 100);
  const processingAmt = revenue * (processing / 100);
  const shipFirst = row.ship_first || 0;
  const shipAdd = row.ship_add != null ? row.ship_add : shipFirst;
  const shipTotal = !absorbShipping ? 0 : (units > 0 ? shipFirst + shipAdd * (units - 1) : 0);
  const productCost = row.unit_cost * units;
  const profit = revenue - commissionAmt - processingAmt - productCost - shipTotal;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  return { revenue, profit, margin, units };
}

// Wholesale price that hits `target`% net margin given cost + fees (+ per-unit shipping
// if the brand absorbs it). Returns null if fees + target ≥ 100% (mathematically impossible).
function suggestedWholesale(row, feePct, target, absorbShipping) {
  const C = row.unit_cost + (absorbShipping ? (row.ship_first || 0) : 0);
  const denom = 1 - feePct / 100 - target / 100;
  if (denom <= 0) return null;
  return Math.ceil((C / denom) * 100) / 100;
}

export default function WholesaleMargins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commission, setCommission] = useState(15);
  const [processing, setProcessing] = useState(3);
  const [openingMin, setOpeningMin] = useState(100);
  const [reorderMin, setReorderMin] = useState(75);
  const [typeFilter, setTypeFilter] = useState("");
  const [absorbShipping, setAbsorbShipping] = useState(true);
  const [target, setTarget] = useState(40);
  const [onlyBelow, setOnlyBelow] = useState(false);
  const [excludedTypes, setExcludedTypes] = useState([]);
  const [faireMin, setFaireMin] = useState(4);
  const [applying, setApplying] = useState(false);
  const [showBench, setShowBench] = useState(false);
  const [benchDraft, setBenchDraft] = useState({});
  const [savingBench, setSavingBench] = useState(false);
  const [normalizePreview, setNormalizePreview] = useState(null);
  const [rebasePreview, setRebasePreview] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const pollJob = (jobId, doneMsg) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        if (["done", "error"].includes(j.status)) {
          clearInterval(pollRef.current);
          setApplying(false);
          if (j.status === "done") toast.success(`${doneMsg} — ${j.result?.updated || 0} product(s)`);
          else toast.error("Job hit an error — check logs");
          load();
          loadSnapshots();
        }
      } catch { clearInterval(pollRef.current); setApplying(false); }
    }, 2500);
  };

  const loadSnapshots = async () => {
    try {
      const { data } = await api.get("/wholesale/price-snapshots");
      setSnapshots(data.snapshots || []);
    } catch { setSnapshots([]); }
  };
  useEffect(() => { loadSnapshots(); }, []);

  const openNormalize = async () => {
    try {
      const { data } = await api.get("/wholesale/normalize-preview");
      if (!data.count) { toast.success("All prices are within their market ranges — nothing to fix."); return; }
      setNormalizePreview(data);
    } catch { toast.error("Could not load price preview"); }
  };

  const runNormalize = async () => {
    const items = (normalizePreview?.items || []).map((i) => ({ id: i.id, wholesale_price: i.new_ws, retail_price: i.new_retail }));
    if (!items.length) return;
    setApplying(true);
    setNormalizePreview(null);
    try {
      const { data } = await api.post("/wholesale/apply-pricing", { items });
      toast.info(`Normalizing ${data.count} product(s) & syncing to Shopify…`);
      pollJob(data.job_id, "Prices normalized");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not normalize");
      setApplying(false);
    }
  };

  const openRebaseline = async () => {
    try {
      const { data } = await api.get("/pricing/rebaseline-preview");
      if (!data.count) { toast.success("Every product is already at your commercial baseline — nothing to change."); return; }
      setRebasePreview(data);
    } catch { toast.error("Could not build rebaseline preview"); }
  };

  const runRebaseline = async () => {
    const items = (rebasePreview?.items || []).map((i) => ({ id: i.id, wholesale_price: i.new_ws, retail_price: i.new_retail }));
    if (!items.length) return;
    setApplying(true);
    setRebasePreview(null);
    try {
      const { data } = await api.post("/wholesale/apply-pricing", { items });
      toast.info(`Rebaselining ${data.count} product(s) & syncing to Shopify…`);
      pollJob(data.job_id, "Retail rebaselined");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rebaseline");
      setApplying(false);
    }
  };

  const revertSnapshot = async (snap) => {
    if (!window.confirm(`Revert ${snap.count} product(s) back to their prices from "${snap.label}" (${new Date(snap.created_at).toLocaleString()})? This restores the previous wholesale + retail and re-syncs to Shopify.`)) return;
    setApplying(true);
    setShowSnapshots(false);
    try {
      const { data } = await api.post(`/wholesale/revert/${snap.id}`);
      toast.info(`Reverting ${data.count} product(s)…`);
      pollJob(data.job_id, "Reverted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
      setApplying(false);
    }
  };

  const load = async (refresh = false) => {
    try {
      const { data } = await api.get(`/wholesale/margins${refresh ? "?refresh=true" : ""}`);
      setData(data);
      setExcludedTypes(data.faire_excluded_types || []);
      setBenchDraft(data.benchmarks || {});
      const d = data.defaults || {};
      setCommission(d.commission_pct ?? 15);
      setProcessing(d.processing_pct ?? 3);
      setOpeningMin(d.opening_min ?? 100);
      setReorderMin(d.reorder_min ?? 75);
      setFaireMin(d.faire_min_margin ?? 4);
      setTarget(d.target_margin ?? 40);
      setAbsorbShipping(d.absorb_shipping ?? true);
      if (data.econ_error) toast.warning(data.econ_error);
    } catch (e) {
      toast.error("Could not load wholesale margins");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(true); };

  // Apply suggested WHOLESALE prices (Faire-only field; no consumer/retail change, no Printify re-sync).
  const applySuggested = async (items) => {
    const payload = items
      .filter((r) => r.sugWs != null && r.sugWs > 0)
      .map((r) => ({ id: r.id, wholesale_price: r.sugWs }));
    if (!payload.length) { toast.info("Nothing to apply — no valid suggestions."); return; }
    const many = payload.length > 1;
    if (!window.confirm(`Update the WHOLESALE price on ${payload.length} product${many ? "s" : ""} to the suggested value? This only affects Faire wholesale margins — your Honorifica.com consumer prices are NOT changed.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/wholesale/apply-suggested", { items: payload });
      toast.success(`Updated wholesale price on ${data.updated} product${data.updated === 1 ? "" : "s"}${data.errors?.length ? ` · ${data.errors.length} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply suggested prices");
    } finally { setApplying(false); }
  };

  // Apply suggested WHOLESALE + RETAIL, and re-sync the new retail to Printify → Shopify
  // (raises the consumer price). Runs as a background job.
  const applyBoth = async (items) => {
    const payload = items
      .filter((r) => r.needsRetailBump && !r.targetUnreachable && r.sugRetail != null && r.sugRetail > 0)
      .map((r) => ({ id: r.id, wholesale_price: Math.round(r.sugRetail * 0.5 * 100) / 100, retail_price: r.sugRetail }));
    if (!payload.length) { toast.info("Nothing to apply — no valid suggestions."); return; }
    const many = payload.length > 1;
    if (!window.confirm(`Raise BOTH wholesale and retail on ${payload.length} product${many ? "s" : ""} to the suggested values?\n\n⚠️ This RAISES the consumer price on Honorifica.com and re-publishes those listings to Shopify. Wholesale becomes 50% of the new retail.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/wholesale/apply-pricing", { items: payload });
      toast.info(`Repricing ${data.count} product${data.count === 1 ? "" : "s"} & syncing to Shopify…`);
      pollJob(data.job_id, "Repriced (store + Faire updated)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reprice");
      setApplying(false);
    }
  };

  const setBench = (t, dim, idx, val) =>
    setBenchDraft((b) => {
      const cur = b[t] || {};
      const rng = [...(cur[dim] || [0, 0])];
      rng[idx] = val === "" ? "" : Number(val);
      return { ...b, [t]: { ...cur, [dim]: rng } };
    });

  const saveBenchmarks = async () => {
    setSavingBench(true);
    try {
      await api.post("/wholesale/benchmarks", { benchmarks: benchDraft });
      toast.success("Market benchmarks saved");
      load();
    } catch {
      toast.error("Could not save benchmarks");
    } finally { setSavingBench(false); }
  };

  const toggleExcludedType = async (t) => {
    const next = excludedTypes.includes(t) ? excludedTypes.filter((x) => x !== t) : [...excludedTypes, t];
    setExcludedTypes(next);
    try {
      await api.post("/faire/excluded-types", { types: next });
      toast.success(`${TYPE_LABELS[t] || t} ${next.includes(t) ? "excluded from" : "allowed on"} Faire`);
      load();
    } catch { toast.error("Could not update Faire exclusions"); }
  };
  const saveDefaults = async () => {
    setSaving(true);
    try {
      await api.post("/wholesale/settings", {
        commission_pct: Number(commission), processing_pct: Number(processing),
        opening_min: Number(openingMin), reorder_min: Number(reorderMin), faire_min_margin: Number(faireMin),
        target_margin: Number(target), absorb_shipping: absorbShipping,
      });
      toast.success("Saved as defaults");
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const c = Number(commission), p = Number(processing);
    const fee = c + p;
    const t = Number(target);
    let list = data.rows.map((r) => {
      const unit = orderEconomics(r, 1, c, p, absorbShipping);
      const openUnits = r.wholesale > 0 ? Math.ceil(Number(openingMin) / r.wholesale) : 0;
      const reUnits = r.wholesale > 0 ? Math.ceil(Number(reorderMin) / r.wholesale) : 0;
      const idealWs = suggestedWholesale(r, fee, t, absorbShipping);
      const bm = data.benchmarks?.[r.product_type];
      // Keystone guardrail: a wholesale price can never exceed retail. WS-only apply is capped
      // at 50% of current retail so it can never set WS above retail.
      const maxWsAtRetail = r.retail > 0 ? Math.round(r.retail * 0.5 * 100) / 100 : null;
      const sugWs = idealWs == null ? null : (maxWsAtRetail != null ? Math.min(idealWs, maxWsAtRetail) : idealWs);
      // "Needs retail bump" = current retail is too low for WS to ever hit the target margin.
      const needsRetailBump = idealWs != null && maxWsAtRetail != null && idealWs > maxWsAtRetail + 0.005;
      // Retail required to hit the target = 2× ideal WS, but never propose above the market ceiling.
      const bmRetailHi = Array.isArray(bm?.retail) ? bm.retail[1] : null;
      const neededRetail = idealWs != null ? Math.ceil(idealWs / 0.5 * 100) / 100 : null;
      const sugRetail = needsRetailBump
        ? Math.round(Math.min(neededRetail, bmRetailHi || neededRetail) * 100) / 100
        : null;
      // Margin actually achievable at the capped WS (when we can't reach target at market retail).
      const achMargin = sugWs != null ? orderEconomics({ ...r, wholesale: sugWs }, 1, c, p, absorbShipping).margin : null;
      // Target unreachable even after clamping retail to the market ceiling.
      const targetUnreachable = needsRetailBump && bmRetailHi != null && neededRetail > bmRetailHi + 0.005;
      return {
        ...r,
        unitProfit: unit.profit,
        unitMargin: unit.margin,
        open: orderEconomics(r, openUnits, c, p, absorbShipping),
        reorder: orderEconomics(r, reUnits, c, p, absorbShipping),
        sugWs,
        idealWs,
        needsRetailBump,
        sugRetail,
        achMargin,
        targetUnreachable,
        newRatio: sugWs != null && r.retail > 0 ? (sugWs / r.retail) * 100 : null,
        belowTarget: unit.margin < t - 0.05,
        bmRetail: bm?.retail,
        bmWholesale: bm?.wholesale,
        retailBand: band(r.retail, bm?.retail),
        wsBand: band(r.wholesale, bm?.wholesale),
      };
    });
    if (typeFilter) list = list.filter((r) => r.product_type === typeFilter);
    if (onlyBelow) list = list.filter((r) => r.belowTarget);
    return list;
  }, [data, commission, processing, openingMin, reorderMin, typeFilter, absorbShipping, target, onlyBelow]);

  const totals = useMemo(() => {
    if (!data) return null;
    const all = rows;
    if (!all.length) return null;
    const avgMargin = all.reduce((s, r) => s + r.unitMargin, 0) / all.length;
    const openProfit = all.reduce((s, r) => s + r.open.profit, 0);
    const reProfit = all.reduce((s, r) => s + r.reorder.profit, 0);
    const negative = all.filter((r) => r.unitProfit <= 0).length;
    const below = all.filter((r) => r.belowTarget).length;
    return { avgMargin, openProfit, reProfit, negative, below, count: all.length };
  }, [rows, data]);

  const types = data ? [...new Set(data.rows.map((r) => r.product_type))] : [];

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="wholesale-margins-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Faire · Wholesale</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Wholesale Margins</h1>
          <p className="text-sm text-zinc-500 mt-1">Real Printify cost + US shipping vs. your wholesale price, after Faire commission — profit &amp; margin at your order floors.</p>
        </div>
        <button onClick={refresh} disabled={refreshing} data-testid="refresh-costs-btn" className="h-9 px-3 rounded-md border border-zinc-300 hover:bg-zinc-100 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh live costs
        </button>
      </div>

      {/* Assumptions bar */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="assumptions-bar">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Calculator className="w-4 h-4 text-blue-600" /> Assumptions</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: "Faire commission %", val: commission, set: setCommission, tid: "input-commission" },
            { label: "Payment processing %", val: processing, set: setProcessing, tid: "input-processing" },
            { label: "Opening order min ($)", val: openingMin, set: setOpeningMin, tid: "input-opening" },
            { label: "Reorder min ($)", val: reorderMin, set: setReorderMin, tid: "input-reorder" },
            { label: "Target WS margin %", val: target, set: setTarget, tid: "input-target" },
            { label: "Min Faire margin %", val: faireMin, set: setFaireMin, tid: "input-faire-min" },
          ].map((f) => (
            <label key={f.tid} className="block">
              <span className="text-[11px] text-zinc-500">{f.label}</span>
              <Input type="number" step="0.1" value={f.val} onChange={(e) => f.set(e.target.value)} data-testid={f.tid} className="mt-1 h-9" />
            </label>
          ))}
          <div className="flex items-end">
            <button onClick={saveDefaults} disabled={saving} data-testid="save-defaults-btn" className="h-9 px-3 w-full rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap border-t border-zinc-100 pt-3">
          <span className="text-[11px] text-zinc-500">Shipping paid by:</span>
          {[["brand", "Brand absorbs it"], ["retailer", "Retailer pays it"]].map(([val, label]) => (
            <button key={val} onClick={() => setAbsorbShipping(val === "brand")} data-testid={`shipping-${val}`}
              className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${(absorbShipping ? "brand" : "retailer") === val ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
              {label}
            </button>
          ))}
          <span className="text-[11px] text-zinc-400 ml-1">On Faire, retailers often pay shipping — toggle to see net margin either way.</span>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap border-t border-zinc-100 pt-3" data-testid="faire-excluded-types">
          <span className="text-[11px] text-zinc-500">Never list on Faire (product types):</span>
          {Object.keys(TYPE_LABELS).map((t) => {
            const on = excludedTypes.includes(t);
            return (
              <button key={t} onClick={() => toggleExcludedType(t)} data-testid={`exclude-type-${t}`}
                className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${on ? "bg-rose-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {on ? "🚫 " : ""}{TYPE_LABELS[t] || t}
              </button>
            );
          })}
          <span className="text-[11px] text-zinc-400 ml-1">Plus, any item below your {Number(faireMin)}% Faire margin floor is auto-blocked (Faire column).</span>
        </div>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="margin-totals">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">Avg. unit margin</div>
            <div className={`text-2xl font-bold mt-1 ${marginTone(totals.avgMargin)}`}>{pct(totals.avgMargin)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">Profit @ opening floor (avg/SKU)</div>
            <div className={`text-2xl font-bold mt-1 ${totals.openProfit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(totals.openProfit / totals.count)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">Profit @ reorder floor (avg/SKU)</div>
            <div className={`text-2xl font-bold mt-1 ${totals.reProfit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(totals.reProfit / totals.count)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="text-[11px] text-zinc-500">SKUs below {Number(target)}% target</div>
            <div className={`text-2xl font-bold mt-1 ${totals.below ? "text-rose-600" : "text-emerald-600"}`}>{totals.below}</div>
          </div>
        </div>
      )}

      {/* Type filter */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="margins-type-filter" className="bg-white border border-zinc-200 rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All product types</option>
          {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
        </select>
        <button onClick={() => setOnlyBelow((v) => !v)} data-testid="toggle-below-target" className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${onlyBelow ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
          {onlyBelow ? "Showing below-target only" : "Show below-target only"}
        </button>
        {(() => {
          const applicable = rows.filter((r) => r.belowTarget && r.sugWs != null && r.sugWs > 0);
          const bumpable = applicable.filter((r) => r.needsRetailBump && !r.targetUnreachable && r.sugRetail > 0);
          if (!applicable.length) return null;
          return (
            <>
              <button onClick={() => applySuggested(applicable)} disabled={applying} data-testid="apply-suggested-all-btn"
                className="h-8 px-3 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                Apply suggested WS to {applicable.length}
              </button>
              {bumpable.length > 0 && (
                <button onClick={() => applyBoth(bumpable)} disabled={applying} data-testid="apply-both-all-btn"
                  className="h-8 px-3 rounded-md text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                  title="Raises consumer retail (up to market ceiling) + wholesale and re-syncs to Shopify">
                  {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                  WS + Retail to {bumpable.length}
                </button>
              )}
            </>
          );
        })()}
        <span className="text-xs text-zinc-400">{rows.length} items</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={openRebaseline} disabled={applying} data-testid="ws-rebaseline-btn"
            className="h-8 px-3 rounded-md text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            title="Reset every product's retail to a commercial baseline (market midpoint or DTC margin floor). WS follows at 50%. Undoable.">
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
            Rebaseline retail
          </button>
          <button onClick={openNormalize} disabled={applying} data-testid="normalize-prices-btn"
            className="h-8 px-3 rounded-md text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            title="Find products priced outside their market range and reset them to sane prices">
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
            Fix out-of-range prices
          </button>
          <div className="relative">
            <button onClick={() => { setShowSnapshots((v) => !v); loadSnapshots(); }} data-testid="undo-menu-btn"
              className="h-8 px-3 rounded-md text-xs font-medium border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100 transition-colors">
              Undo recent change{snapshots.length ? ` (${snapshots.length})` : ""}
            </button>
            {showSnapshots && (
              <div className="absolute right-0 mt-1 w-80 max-h-80 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg z-20 p-2" data-testid="snapshots-menu">
                {snapshots.length === 0 ? (
                  <div className="text-xs text-zinc-400 px-2 py-3 text-center">No recent price changes to undo.</div>
                ) : snapshots.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded" data-testid={`snapshot-${s.id}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-zinc-700 truncate">{s.label}</div>
                      <div className="text-[10px] text-zinc-400">{s.count} items · {new Date(s.created_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => revertSnapshot(s)} disabled={applying} data-testid={`revert-${s.id}`}
                      className="h-6 px-2 rounded text-[11px] font-medium bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-50 shrink-0">Revert</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setShowBench((v) => !v)} data-testid="toggle-benchmarks-btn"
            className="h-8 px-3 rounded-md text-xs font-medium border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100 transition-colors">
            {showBench ? "Hide market benchmarks" : "Edit market benchmarks"}
          </button>
        </div>
      </div>

      {rebasePreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ws-rebaseline-modal" onClick={() => setRebasePreview(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-100">
              <h3 className="text-sm font-bold text-zinc-800">Rebaseline retail to commercial prices</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{rebasePreview.count} product(s) will change. Retail = market midpoint or the price for a {rebasePreview.target_margin}% DTC margin (whichever is higher), charm-priced. Wholesale follows at 50% (never below cost). This changes consumer + wholesale prices and re-syncs to Shopify. You can undo it afterward.</p>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              <table className="w-full text-xs">
                <thead className="text-zinc-400 sticky top-0 bg-white">
                  <tr><th className="text-left px-2 py-1.5">Product</th><th className="text-right px-2 py-1.5">Retail</th><th className="text-right px-2 py-1.5">WS</th><th className="text-right px-2 py-1.5">DTC margin</th></tr>
                </thead>
                <tbody>
                  {rebasePreview.items.map((i) => (
                    <tr key={i.id} className="border-t border-zinc-50" data-testid={`ws-rebaseline-item-${i.id}`}>
                      <td className="px-2 py-1.5"><div className="font-medium text-zinc-700 truncate max-w-[280px]">{i.title}</div><div className="text-[10px] text-zinc-400">{TYPE_LABELS[i.product_type] || i.product_type}</div></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_retail)}</span> <span className="font-semibold text-emerald-700">{money(i.new_retail)}</span></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_ws)}</span> <span className="font-semibold text-emerald-700">{money(i.new_ws)}</span></td>
                      <td className={`text-right px-2 py-1.5 tabular-nums font-semibold ${i.dtc_margin >= 45 ? "text-emerald-600" : "text-amber-600"}`}>{i.dtc_margin}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-zinc-100 flex items-center justify-end gap-2">
              <button onClick={() => setRebasePreview(null)} data-testid="ws-rebaseline-cancel-btn" className="h-9 px-4 rounded-md border border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button onClick={runRebaseline} data-testid="ws-rebaseline-apply-btn" className="h-9 px-4 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold">Apply to {rebasePreview.count} & sync</button>
            </div>
          </div>
        </div>
      )}

      {normalizePreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="normalize-modal" onClick={() => setNormalizePreview(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-100">
              <h3 className="text-sm font-bold text-zinc-800">Fix out-of-range prices</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{normalizePreview.count} product(s) priced outside their market range. Review old → new before applying. This changes consumer + wholesale prices and re-syncs to Shopify. You can undo it afterward.</p>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              <table className="w-full text-xs">
                <thead className="text-zinc-400 sticky top-0 bg-white">
                  <tr><th className="text-left px-2 py-1.5">Product</th><th className="text-right px-2 py-1.5">Retail</th><th className="text-right px-2 py-1.5">WS</th></tr>
                </thead>
                <tbody>
                  {normalizePreview.items.map((i) => (
                    <tr key={i.id} className="border-t border-zinc-50" data-testid={`normalize-item-${i.id}`}>
                      <td className="px-2 py-1.5"><div className="font-medium text-zinc-700 truncate max-w-[280px]">{i.title}</div><div className="text-[10px] text-zinc-400">{TYPE_LABELS[i.product_type] || i.product_type}</div></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_retail)}</span> <span className="font-semibold text-emerald-700">{money(i.new_retail)}</span></td>
                      <td className="text-right px-2 py-1.5 tabular-nums"><span className="text-zinc-400 line-through">{money(i.current_ws)}</span> <span className="font-semibold text-emerald-700">{money(i.new_ws)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-zinc-100 flex items-center justify-end gap-2">
              <button onClick={() => setNormalizePreview(null)} data-testid="normalize-cancel-btn" className="h-9 px-4 rounded-md border border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button onClick={runNormalize} data-testid="normalize-apply-btn" className="h-9 px-4 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">Apply to {normalizePreview.count} & sync</button>
            </div>
          </div>
        </div>
      )}

      {showBench && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="benchmarks-editor">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="text-sm font-bold text-zinc-800">Market benchmark ranges</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Retail = typical Amazon consumer price · Wholesale = typical Faire price. Your prices are compared to these in the Market column. Editable per type.</p>
            </div>
            <button onClick={saveBenchmarks} disabled={savingBench} data-testid="save-benchmarks-btn"
              className="h-8 px-3 rounded-md text-xs font-semibold bg-zinc-900 hover:bg-zinc-800 text-white flex items-center gap-1.5 disabled:opacity-50 transition-colors">
              {savingBench ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save benchmarks
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {Object.keys(TYPE_LABELS).filter((t) => benchDraft[t]).map((t) => (
              <div key={t} className="border border-zinc-100 rounded-md p-2.5" data-testid={`bench-row-${t}`}>
                <div className="text-xs font-semibold text-zinc-700 mb-1.5">{TYPE_LABELS[t]}</div>
                <div className="flex items-center gap-3 text-[11px]">
                  <div className="flex items-center gap-1">
                    <span className="text-zinc-400 w-9">Retail</span>
                    <input type="number" value={benchDraft[t]?.retail?.[0] ?? ""} onChange={(e) => setBench(t, "retail", 0, e.target.value)} data-testid={`bench-${t}-retail-lo`} className="w-14 h-7 px-1.5 border border-zinc-200 rounded text-right" />
                    <span className="text-zinc-300">–</span>
                    <input type="number" value={benchDraft[t]?.retail?.[1] ?? ""} onChange={(e) => setBench(t, "retail", 1, e.target.value)} data-testid={`bench-${t}-retail-hi`} className="w-14 h-7 px-1.5 border border-zinc-200 rounded text-right" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-zinc-400 w-6">WS</span>
                    <input type="number" value={benchDraft[t]?.wholesale?.[0] ?? ""} onChange={(e) => setBench(t, "wholesale", 0, e.target.value)} data-testid={`bench-${t}-ws-lo`} className="w-14 h-7 px-1.5 border border-zinc-200 rounded text-right" />
                    <span className="text-zinc-300">–</span>
                    <input type="number" value={benchDraft[t]?.wholesale?.[1] ?? ""} onChange={(e) => setBench(t, "wholesale", 1, e.target.value)} data-testid={`bench-${t}-ws-hi`} className="w-14 h-7 px-1.5 border border-zinc-200 rounded text-right" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="margins-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-3">Product</th>
              <th className="text-right font-medium px-3 py-3">WS</th>
              <th className="text-right font-medium px-3 py-3">Unit cost</th>
              <th className="text-right font-medium px-3 py-3">Ship</th>
              <th className="text-right font-medium px-3 py-3">Unit profit</th>
              <th className="text-right font-medium px-3 py-3">Margin</th>
              <th className="text-right font-medium px-3 py-3">Opening ({money(openingMin)})</th>
              <th className="text-right font-medium px-3 py-3">Reorder ({money(reorderMin)})</th>
              <th className="text-right font-medium px-4 py-3">Suggested (@{Number(target)}%)</th>
              <th className="text-center font-medium px-3 py-3">Market<br/><span className="normal-case text-[10px] text-zinc-400">Amazon retail · Faire WS</span></th>
              <th className="text-center font-medium px-3 py-3">Faire</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`margin-row-${r.id}`} className={`hover:bg-zinc-50/70 ${r.belowTarget ? (r.unitMargin < 5 ? "bg-rose-50/50" : "bg-amber-50/50") : ""}`}>
                <td className="px-4 py-3 max-w-[340px]">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {r.belowTarget && <span title={r.unitMargin < 5 ? "Critical: margin below 5% / at a loss" : "Warning: margin below target"} data-testid={`flag-${r.id}`}><AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${r.unitMargin < 5 ? "text-rose-500" : "text-amber-500"}`} /></span>}
                    <span className="truncate">{r.title}</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 flex items-center gap-1">
                    {TYPE_LABELS[r.product_type] || r.product_type}
                    {r.cost_is_estimate && <span className="inline-flex items-center gap-0.5 text-amber-600" title="Using estimated cost (no live Printify listing for this type)"><AlertTriangle className="w-3 h-3" /> est.</span>}
                  </div>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">{money(r.wholesale)}</td>
                <td className="text-right px-3 py-3 tabular-nums text-zinc-500">{money(r.unit_cost)}</td>
                <td className="text-right px-3 py-3 tabular-nums text-zinc-500">{r.ship_first != null ? (absorbShipping ? money(r.ship_first) : "—") : "—"}</td>
                <td className={`text-right px-3 py-3 tabular-nums font-semibold ${r.unitProfit <= 0 ? "text-rose-600" : "text-zinc-800"}`}>{money(r.unitProfit)}</td>
                <td className={`text-right px-3 py-3 tabular-nums font-semibold ${marginTone(r.unitMargin)}`}>{pct(r.unitMargin)}</td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <div className={`font-semibold ${r.open.profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(r.open.profit)}</div>
                  <div className="text-[11px] text-zinc-400">{r.open.units} u · {pct(r.open.margin)}</div>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">
                  <div className={`font-semibold ${r.reorder.profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(r.reorder.profit)}</div>
                  <div className="text-[11px] text-zinc-400">{r.reorder.units} u · {pct(r.reorder.margin)}</div>
                </td>
                <td className="text-right px-4 py-3 tabular-nums" data-testid={`suggest-${r.id}`}>
                  {!r.belowTarget ? (
                    <span className="text-[11px] text-emerald-600 font-medium">✓ on target</span>
                  ) : r.sugWs == null ? (
                    <span className="text-[11px] text-zinc-400" title="Fees + target ≥ 100%">n/a</span>
                  ) : (
                    <>
                      <div className="font-semibold text-blue-700">WS {money(r.sugWs)}{r.needsRetailBump ? "*" : ""}</div>
                      {r.needsRetailBump ? (
                        <>
                          <div className="text-[11px] text-zinc-500">= {pct(r.achMargin)} margin at {money(r.retail)} retail</div>
                          {r.targetUnreachable ? (
                            <div className="text-[11px] text-rose-600" title="Even at the top of the market retail range, this item can't reach your target wholesale margin — it isn't viable for Faire at a realistic price.">*{Number(target)}% not reachable at market prices</div>
                          ) : (
                            <div className="text-[11px] text-amber-600" title="Wholesale can't exceed retail. To hit your target margin you'd need to raise the consumer retail price to the amount shown.">*needs retail {money(r.sugRetail)} (WS {money(Math.round(r.sugRetail * 0.5 * 100) / 100)}) to hit {Number(target)}%</div>
                          )}
                        </>
                      ) : (
                        <div className="text-[11px] text-emerald-600">hits {Number(target)}% — retail unchanged</div>
                      )}
                      <div className="mt-1 flex items-center justify-end gap-1">
                        <button onClick={() => applySuggested([r])} disabled={applying} data-testid={`apply-suggested-${r.id}`}
                          className="h-6 px-2 rounded text-[11px] font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 disabled:opacity-50 transition-colors">
                          WS
                        </button>
                        {r.needsRetailBump && !r.targetUnreachable && (
                          <button onClick={() => applyBoth([r])} disabled={applying} data-testid={`apply-both-${r.id}`}
                            title={`Raise retail to ${money(r.sugRetail)} + WS and sync to Shopify`}
                            className="h-6 px-2 rounded text-[11px] font-medium bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 disabled:opacity-50 transition-colors">
                            WS + Retail
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </td>
                <td className="text-center px-3 py-3" data-testid={`market-${r.id}`}>
                  <div className="flex flex-col items-center gap-1">
                    {r.retailBand ? (
                      <span className={`inline-flex items-center rounded-full text-[10px] font-medium px-2 py-0.5 ${BAND_STYLE[r.retailBand].cls}`} title={r.bmRetail ? `Retail market ${money(r.bmRetail[0])}–${money(r.bmRetail[1])} (Amazon)` : ""}>
                        Retail {BAND_STYLE[r.retailBand].label}
                      </span>
                    ) : <span className="text-[10px] text-zinc-300">—</span>}
                    {r.wsBand ? (
                      <span className={`inline-flex items-center rounded-full text-[10px] font-medium px-2 py-0.5 ${BAND_STYLE[r.wsBand].cls}`} title={r.bmWholesale ? `Wholesale market ${money(r.bmWholesale[0])}–${money(r.bmWholesale[1])} (Faire)` : ""}>
                        WS {BAND_STYLE[r.wsBand].label}
                      </span>
                    ) : <span className="text-[10px] text-zinc-300">—</span>}
                  </div>
                </td>
                <td className="text-center px-3 py-3" data-testid={`faire-${r.id}`}>
                  {r.faire_eligible ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-0.5" title={`Faire margin ${r.faire_margin}%`}>OK</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-rose-50 text-rose-700 text-[11px] font-medium px-2 py-0.5" title={r.faire_reason === "type_excluded" ? "Product type excluded from Faire" : `Faire margin ${r.faire_margin}% is below your ${Number(faireMin)}% floor`}>
                      🚫 {r.faire_reason === "type_excluded" ? "Excluded" : `${r.faire_margin}%`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="py-16 text-center text-sm text-zinc-400 flex flex-col items-center gap-2"><TrendingUp className="w-8 h-8 text-zinc-300" /> No products to analyze yet.</div>
        )}
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">Floor profit assumes you absorb Printify product cost + US shipping (first item + additional items) for the whole order; commission &amp; processing are taken on wholesale revenue. Costs are pulled live from your Printify listings.</p>
    </div>
  );
}
